import React, { Component } from 'react'
import { Router, navigate } from '@reach/router'
import 'bootstrap/dist/css/bootstrap.min.css'
import './styles/specialquiz-admin.css'
import jwt from 'jsonwebtoken'

import Home from './pages/HomePage'
import CreateQuestion from './pages/question/CreateQuestionPage'
import QuestionsPage from './pages/question/QuestionsPage'
import CreateTopicPage from './pages/topic/CreateTopicPage'
import TopicsPage from './pages/topic/TopicsPage'
import CreatePackPage from './pages/pack/CreatePackPage'
import PacksPage from './pages/pack/PacksPage'
import CreateUser from './pages/user/CreateUserPage'
import UsersPage from './pages/user/UsersPage'
import LoginPage from './pages/LoginPage'
import AppStore from './AppStore'
import ExportsPage from './pages/ExportsPage/ExportsPage'
import QuestionDetail from './pages/question/QuestionDetailPage'
import RevisionPage from './pages/question/RevisionPage'
import axios from 'axios'
import SubTopicsPage from './pages/subtopic/SubTopicsPage'
import CreatesubTopicPage from './pages/subtopic/CreateSubTopicPage'
import CreateArticlePage from './pages/articles/CreateArticlePage'
import ArticlesPage from './pages/articles/ArticlesPage'
import ReferralCampaignsPage from './pages/referralCampaign/ReferralCampaignsPage'
import CreateReferralCampaignPage from './pages/referralCampaign/CreateReferralCampaignPage'
import { UserApi } from 'api'
import Button from 'react-bootstrap/Button'
import Spinner from 'react-bootstrap/Spinner'
import { Brand } from 'components'

const NotFound = (props: any) => (
  <main className="sq-system-state">
    <section className="sq-system-state__card">
      <Brand subtitle="Gestionale autori" />
      <h1>Pagina non trovata</h1>
      <p>La pagina richiesta non esiste oppure è stata spostata.</p>
      <Button href="/" variant="primary">Torna alla home</Button>
    </section>
  </main>
)

interface Props {}

interface State {
  loading: boolean
  message?: string
  errorMessage?: string
  linkOperation?: 'resetPassword' | 'confirmUser'
  resetPassword?: string
  cofirmResetPassword?: string
}

export default class App extends Component<Props, State> {
  state: State = { loading: true, linkOperation: undefined }
  token: string = ''

  configureApiClient = () => {
    axios.defaults.headers.common['accesstoken'] = AppStore.user?.accessToken
    axios.defaults.headers.common['appversion'] = '2.0.11'
  }

  getAccessTokenExpiration = (accessToken: string): number | undefined => {
    const decodedToken = jwt.decode(accessToken)
    if (!decodedToken || typeof decodedToken === 'string' || typeof decodedToken.exp !== 'number') return undefined
    return decodedToken.exp
  }

  redirectToLogin = async (message: string) => {
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`
    const redirectUrl = window.location.pathname === '/login' ? '/' : currentUrl

    await AppStore.clearAsyncStorage()
    delete axios.defaults.headers.common['accesstoken']
    await AppStore.saveRedirectUrl(redirectUrl)
    await AppStore.changeNotification({ message, type: 'error' })
    navigate('/login')
  }

  async componentDidMount() {
    let linkOperation: State['linkOperation']

    try {
      linkOperation = await this.getLinkOperationIfExist(window.location.pathname)
      if (linkOperation === undefined) {
        await AppStore.loadInitialData()

        const accessToken = AppStore.user?.accessToken
        const refreshToken = AppStore.user?.refreshToken

        if (!accessToken || !refreshToken) {
          await this.redirectToLogin('Effettua il login prima di accedere al sito.')
          return
        }

        this.configureApiClient()

        const expiresAt = this.getAccessTokenExpiration(accessToken)
        if (expiresAt === undefined) {
          await this.redirectToLogin('La sessione salvata non è più valida. Effettua nuovamente il login.')
          return
        }

        const refreshThreshold = Date.now() / 1000 + 24 * 60 * 60
        if (expiresAt <= refreshThreshold) {
          try {
            await UserApi.refreshToken(refreshToken)
            this.configureApiClient()
          } catch (error) {
            await this.redirectToLogin('La sessione è scaduta. Effettua nuovamente il login.')
            return
          }
        }

        if (window.location.pathname === '/login') navigate('/')
      }
    } catch (error) {
      console.error('Unable to initialize the application session', error)
      if (linkOperation === undefined) {
        await this.redirectToLogin('Non è stato possibile recuperare la sessione. Effettua nuovamente il login.')
      }
    } finally {
      this.setState({ loading: false, linkOperation })
    }
  }

  getLinkOperationIfExist = async (path: string) => {
    const operation = path.substring(path.indexOf('/', 1), path.lastIndexOf('/'))
    this.token = path.substring(path.lastIndexOf('/') + 1)
    if (operation === '/links/resetpassword') {
      return 'resetPassword'
    }
    if (operation === '/links/confirmuser') {
      try {
        await UserApi.confirmEmail(this.token)
        this.setState({ message: 'La tua email è stata validata, grazie' })
      } catch (e) {
        this.setState({ errorMessage: 'Validazione email fallita' })
      }
      return 'confirmUser'
    }
    return undefined
  }

  resetPassword = async () => {
    const { resetPassword, cofirmResetPassword } = this.state

    if (!resetPassword || resetPassword.length < 8) {
      this.setState({ errorMessage: 'La password deve essere lunga almeno 8 caratteri' })
      return
    }

    if (resetPassword !== cofirmResetPassword) {
      this.setState({ errorMessage: 'Le due password non coincidono' })
      return
    }

    var passw = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{6,20}$/
    if (!resetPassword.match(passw)) {
      this.setState({
        errorMessage: 'La password deve contenere almeno un numero e una lettera maiuscola ',
      })
      return
    }

    // FINE CONTROLLI

    try {
      await UserApi.resetPassword(resetPassword, this.token)
      this.setState({ message: "Password cambiata, riaccedi sull'app con la nuova password" })
    } catch (e) {
      this.setState({ errorMessage: 'Cambio password già effettuato con questo link' })
    }
  }

  renderMessage = () => {
    const { message } = this.state
    if (message) return <h2>{message}</h2>
  }

  renderErrorMessage = () => {
    const { errorMessage } = this.state
    if (errorMessage) return <p className="sq-form-error" role="alert">{errorMessage}</p>
  }

  render() {
    const { loading, message, linkOperation } = this.state

    if (loading) {
      return (
        <main className="sq-system-state">
          <section className="sq-system-state__card" role="status">
            <Brand subtitle="Gestionale autori" />
            <div className="sq-loading-indicator"><Spinner animation="border" /></div>
            <p>Caricamento del workspace…</p>
          </section>
        </main>
      )
    }

    if (linkOperation === 'confirmUser') {
      return (
        <main className="sq-system-state">
          <section className="sq-system-state__card">
            <Brand subtitle="Gestionale autori" />
            {this.renderMessage()}
            {this.renderErrorMessage()}
            <Button href="/login" variant="primary">Vai al login</Button>
          </section>
        </main>
      )
    }

    if (linkOperation === 'resetPassword') {
      return (
        <main className="sq-reset-page">
          <section className="sq-system-state__card">
            <Brand subtitle="Gestionale autori" />
            {!message ? (
              <>
                <h1>Nuova password</h1>
                <p>Scegli una password sicura di almeno otto caratteri.</p>
                <label htmlFor="reset-password">Nuova password</label>
                <input id="reset-password" className="form-control" type="password" placeholder="••••••••" onChange={(e) => this.setState({ resetPassword: e.target.value })} />
                <label htmlFor="confirm-password">Conferma password</label>
                <input id="confirm-password" className="form-control" type="password" placeholder="••••••••" onChange={(e) => this.setState({ cofirmResetPassword: e.target.value })} />
                {this.renderErrorMessage()}
                <Button onClick={this.resetPassword} variant="primary">Conferma password</Button>
              </>
            ) : this.renderMessage()}
          </section>
        </main>
      )
    }

    return (
      <div className="application">
        <Router basepath="/">
          <Home path="/" />
          <LoginPage path="/login" />
          <QuestionsPage path="/questions" />
          {/* <QuestionsPage path="/questions/saved" showAllQuestionsStored={true} /> */}
          <QuestionDetail path="/questions/:questionId" />
          <CreateQuestion path="/question/edit/:questionId" />
          <CreateQuestion path="/question/create" />
          <RevisionPage path="/revision" />
          <TopicsPage path="/topics" />
          <CreateTopicPage path="/topic/edit/:topicId" />
          <CreateTopicPage path="/topic/create" />
          <SubTopicsPage path="/subtopics" />
          <CreatesubTopicPage path="/subtopic/edit/:subtopicId" />
          <CreatesubTopicPage path="/subtopic/create" />
          <PacksPage path="/packs" />
          <CreatePackPage path="/pack/edit/:packId" />
          <CreatePackPage path="/pack/create" />
          <UsersPage path="/users" />
          <CreateUser path="/user/edit/:userId" />
          <CreateUser path="/user/create" />
          <ExportsPage path="/imports" />
          <ArticlesPage path="/articles" />
          <CreateArticlePage path="/articles/create" />
          <ReferralCampaignsPage path="/referral-campaigns" />
          <CreateReferralCampaignPage path="/referral-campaign/create" />
          <CreateReferralCampaignPage path="/referral-campaign/edit/:campaignKey" />
          <CreateArticlePage path="/articles/edit/:articleId" />
          <NotFound default />
        </Router>
      </div>
    )
  }
}
